import { ChangeDetectorRef, Component, HostListener, NgZone, OnInit } from '@angular/core';
import { take } from 'rxjs/operators';
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { MessageService } from 'primeng/api';
import {
  FileService,
  UploadProgress,
  MAX_FILE_SIZE_BYTES,
} from '../../services/file.service';
import { FileModel } from '../../models/file';
import { acceptLabel, accepts, filesOf, hasOwnName, isLinkText, linkCapNote, linkPlaceholder, pasteKeys, pastedFileName, sizeLabel, typeError } from './file-upload-rules';

// Config passed via DynamicDialogConfig.data when opening the modal.
export interface FileUploadModalData {
  title?: string; // dialog header
  bucketPrefix: string; // S3 prefix, e.g. 'service-provider-images'
  accept?: string; // input accept filter, e.g. 'image/*' or '.pdf'
  multiple?: boolean; // allow multiple files (default true)
  maxSizeBytes?: number; // per-file cap (default MAX_FILE_SIZE_BYTES)
  pasteName?: string; // a pasted file's name, numbered: "St. Nicolas' Anglican Church 1.png" (default "Pasted image 1.png")
  // "From a link" (2026-11-03) is on for every caller: the server fetches the link and stores it as an upload is, under
  // bucketPrefix, by this dialog's accept and maxSizeBytes. `noLink` turns it off.
  noLink?: boolean;
}

// Result handed back via ref.close(). This is the modal's "@Output" — the caller
// decides what to persist (service media, arrangement doc, etc.). The modal itself
// knows nothing about service providers or arrangements.
export interface FileUploadResult {
  files: FileModel[];
  /** A file from a link: what was pasted (the page's link, when an image was picked from one), for a credit / source. */
  sourceUrl?: string;
  /** A file from a link: the file's own link. */
  fileUrl?: string;
}

interface QueuedFile {
  raw: File;
  name: string;
  type: string;
  size: number;
  percent: number;
  error?: string;
}

@Component({
  selector: 'app-file-upload',
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.scss'],
})
export class FileUploadComponent implements OnInit {
  data!: FileUploadModalData;
  queue: QueuedFile[] = [];
  uploading = false;
  /** A file is being dragged over the drop zone. */
  dragging = false;
  /** A paste that had no file in it (a copied link or text): what to do instead. */
  pasteHint = '';
  maxSizeLabel = '';
  /** "From a link": what's typed, a page's images to pick from, the fetch's state. */
  linkUrl = '';
  linkImages: string[] | null = null;
  linkPage = '';
  linkBusy = false;
  linkError = '';
  typesLabel: string | null = null;
  /** " (25 MB from a link)" where the caller's cap is higher. */
  linkCap = '';
  /** "⌘V" or "Ctrl+V". */
  readonly pasteKeys = pasteKeys(typeof navigator !== 'undefined' ? navigator.platform : '');
  private pasted = 0;

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    private fileService: FileService,
    private messageService: MessageService,
    private cdr: ChangeDetectorRef,
    private zone: NgZone
  ) {}

  ngOnInit(): void {
    this.data = {
      multiple: true,
      accept: '*',
      maxSizeBytes: MAX_FILE_SIZE_BYTES,
      ...(this.config.data as FileUploadModalData),
    };
    this.maxSizeLabel = sizeLabel(this.data.maxSizeBytes);
    this.typesLabel = acceptLabel(this.data.accept);
    this.linkCap = linkCapNote(this.data.maxSizeBytes, this.data.noLink);
    if (!this.data.bucketPrefix) {
      // Fail loud rather than silently uploading to the wrong place.
      this.messageService.add({
        severity: 'error',
        summary: 'Upload misconfigured',
        detail: 'No storage location (bucketPrefix) was provided.',
      });
    }
  }

  onSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files) return;
    this.add(Array.from(input.files));
    input.value = ''; // allow re-selecting the same file
  }

  // Files dropped on the drop zone, as well as chosen (Ivan 2026-11-03).
  onDragOver(event: DragEvent): void {
    if (this.uploading || !Array.from(event.dataTransfer?.types ?? []).includes('Files')) return;
    event.preventDefault();
    if (!this.dragging) {
      this.dragging = true;
      this.cdr.detectChanges();
    }
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.dragging = false;
    if (!this.uploading) this.add(filesOf(event.dataTransfer));
    // The app draws in animation frames; a document-level drop or paste isn't sure to start one: draw now.
    this.cdr.detectChanges();
  }

  // Cmd+V / Ctrl+V while the dialog is open: a copied image (or file) joins the queue, as in Slack or Claude.
  @HostListener('document:paste', ['$event'])
  onPaste(event: ClipboardEvent): void {
    if (this.uploading) return;
    const files = filesOf(event.clipboardData);
    // Text pasted into the link box itself lands there, as typed: no hint, no fetch until "From a link".
    const inLinkBox = !!(event.target as HTMLElement)?.closest?.('.link-box');
    if (!files.length && inLinkBox) return;
    // A pasted link (anywhere else in the dialog): fetched by the server.
    const text = (event.clipboardData?.getData('text/plain') ?? '').trim();
    if (!files.length && !this.data.noLink && isLinkText(text)) {
      event.preventDefault();
      this.linkUrl = text;
      this.fetchLink();
      return;
    }
    if (!files.length) {
      const html = event.clipboardData?.getData('text/html') ?? '';
      this.pasteHint = /<img\b/i.test(html)
        ? 'That copied the picture\'s link, not the picture: right-click the image and choose "Copy Image", then paste again.'
        : 'Nothing to upload on the clipboard: copy an image or a file, then paste.';
      this.cdr.detectChanges();
      return;
    }
    event.preventDefault();
    this.pasteHint = '';
    // A file copied in Finder or Explorer keeps its name (a download's label is made from it); a copied picture has none.
    this.add(files.map((f) => (hasOwnName(f.name) ? f : new File([f], pastedFileName(this.data.pasteName, ++this.pasted, f.type), { type: f.type, lastModified: f.lastModified }))));
    this.cdr.detectChanges();
  }

  /** Into the queue, each checked: the caller's types (a drop or a paste skips the picker's filter) and its size. */
  private add(files: File[]): void {
    const max = this.data.maxSizeBytes ?? MAX_FILE_SIZE_BYTES;
    if (this.data.multiple === false && files.length) this.queue = [];
    for (const f of this.data.multiple === false ? files.slice(0, 1) : files) {
      const error = !accepts(this.data.accept, f)
        ? typeError(this.typesLabel)
        : f.size > max
          ? `Exceeds ${this.maxSizeLabel}`
          : undefined;
      this.queue.push({ raw: f, name: f.name, type: f.type, size: f.size, percent: 0, error });
    }
  }

  get linkPlaceholder(): string {
    return linkPlaceholder(this.data?.accept);
  }

  /** Fetch the link (or, with `image`, the image picked from a page): a file closes the dialog with it. */
  fetchLink(image?: string): void {
    const url = (image ?? this.linkUrl).trim();
    if (this.data.noLink || !url || this.linkBusy) return;
    this.linkBusy = true;
    this.linkError = '';
    this.pasteHint = '';
    const accept = this.data.accept && this.data.accept !== '*' ? this.data.accept : '';
    this.fileService
      .fromLink({ url, prefix: this.data.bucketPrefix, accept, maxBytes: this.data.maxSizeBytes, pageUrl: image ? this.linkPage : undefined })
      .pipe(take(1))
      .subscribe({
        next: (r) =>
          this.zone.run(() => {
            this.linkBusy = false;
            if (r.files?.length) {
              this.ref.close({ files: r.files, sourceUrl: r.sourceUrl, fileUrl: r.fileUrl } as FileUploadResult);
              return;
            }
            this.linkImages = r.images ?? [];
            this.linkPage = r.pageUrl ?? url;
            this.cdr.detectChanges();
          }),
        error: (e) =>
          this.zone.run(() => {
            this.linkBusy = false;
            this.linkError = e?.error?.error?.data?.reason ?? e?.error?.error?.message?.[0] ?? e?.message ?? 'That link couldn\'t be fetched';
            this.cdr.detectChanges();
          }),
      });
    this.cdr.detectChanges();
  }

  removeAt(i: number): void {
    if (this.uploading) return;
    this.queue.splice(i, 1);
  }

  get canUpload(): boolean {
    return (
      !this.uploading &&
      !!this.data?.bucketPrefix &&
      this.queue.length > 0 &&
      this.queue.every((q) => !q.error)
    );
  }

  upload(): void {
    if (!this.canUpload) return;
    this.uploading = true;

    const onProgress = (p: UploadProgress) => {
      const item = this.queue.find((q) => q.name === p.fileName);
      if (item) item.percent = p.percent;
    };

    this.fileService
      .uploadFilesWithPreSignedUrl(
        {
          prefix: this.data.bucketPrefix,
          uploadData: this.queue.map((q) => ({
            fileName: q.name,
            fileType: q.type,
            fileRawContent: q.raw,
          })),
        },
        onProgress
      )
      .subscribe({
        next: (res: { files: FileModel[] }) => {
          this.uploading = false;
          this.messageService.add({
            severity: 'success',
            summary: 'Upload complete',
            detail: `${res.files.length} file(s) uploaded.`,
          });
          // Hand the file records back to the caller. Persistence is the caller's job.
          this.ref.close({ files: res.files } as FileUploadResult);
        },
        error: (err) => {
          this.uploading = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Upload failed',
            detail: err?.message ?? 'Unknown error',
          });
        },
      });
  }

  cancel(): void {
    if (this.uploading) return;
    this.ref.close(undefined); // undefined result = cancelled, nothing to persist
  }
}
